// store/slices/usersSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { loadStateAdmin, loadConfigAdmin } from "../localStorage";

/* ================= THUNKS (API) ================= */

/**
 * Login de usuario
 */
export const loginUser = createAsyncThunk(
  "users/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API}users/login`,
        { username, password }
      );
      return data.login;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

/**
 * Alta de usuario
 */
export const addUser = createAsyncThunk(
  "users/add",
  async (userNew, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API}users/add`,
        userNew
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

/**
 * Actualizar usuario
 */
export const updateUser = createAsyncThunk(
  "users/update",
  async (user, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API}users/update`,
        { user },
        {
          headers: {
            "x-access-token": user.token,
          },
        }
      );
      return data.user;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

/**
 * Obtener configuración
 */
export const fetchConfig = createAsyncThunk(
  "users/getConfig",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}configs/${id}`
      );
      return data.config;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

/**
 * Actualizar configuración
 */
export const updateConfig = createAsyncThunk(
  "users/updateConfig",
  async ({ config, token }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API}configs`,
        config,
        {
          headers: {
            "x-access-token": token,
          },
        }
      );
      return data.config;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

/**
 * Obtener todos los usuarios (admin)
 */
export const fetchAllUsers = createAsyncThunk(
  "users/fetchAll",
  async (token, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}users`,
        {
          headers: {
            "x-access-token": token,
          },
        }
      );
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message
      );
    }
  }
);

/* ================= SLICE ================= */

const usersSlice = createSlice({
  name: "users",
  initialState: {
    userDetail: loadStateAdmin() ?? {},
    configsAdmin: loadConfigAdmin() ?? {},
    allUsers: {},
    userNew: null,
    loading: false,
    error: null,
  },

  reducers: {
    /* -------- LOGOUT -------- */
    logoutUser(state) {
      state.userDetail = {};
      state.configsAdmin = {};
      state.allUsers = {};
      state.userNew = null;

      // Limpieza localStorage (igual que antes)
      localStorage.removeItem("userInfo");
      localStorage.removeItem("userUpdated");
      localStorage.removeItem("allowLogin");
      localStorage.removeItem("categoryDeleted");
      localStorage.removeItem("categoryAdded");
      localStorage.removeItem("categoryUpdated");
      localStorage.removeItem("productDeleted");
      localStorage.removeItem("productAdded");
      localStorage.removeItem("productUpdated");
      localStorage.removeItem("configUpdated");
      localStorage.removeItem("userConfig");
      localStorage.removeItem("appConfig");
      localStorage.removeItem("brandDeleted");
      localStorage.removeItem("brandAdded");
      localStorage.removeItem("brandUpdated");
    },

    clearUserError(state) {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ===== LOGIN ===== */
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userDetail = action.payload;
        localStorage.setItem(
          "userInfo",
          JSON.stringify(action.payload)
        );
        localStorage.setItem("allowLogin", "si");
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== ADD USER ===== */
      .addCase(addUser.fulfilled, (state, action) => {
        state.userNew = action.payload;
        localStorage.setItem("userAdded", true);
      })

      /* ===== UPDATE USER ===== */
      .addCase(updateUser.fulfilled, (state, action) => {
        state.userDetail = action.payload;
        localStorage.setItem(
          "userInfo",
          JSON.stringify(action.payload)
        );
        localStorage.setItem("userUpdated", true);
      })

      /* ===== CONFIG ===== */
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.configsAdmin = action.payload;
        localStorage.setItem(
          "appConfig",
          JSON.stringify(action.payload)
        );
        localStorage.setItem("userConfig", true);
      })

      .addCase(updateConfig.fulfilled, (state, action) => {
        state.configsAdmin = action.payload;
        localStorage.setItem(
          "appConfig",
          JSON.stringify(action.payload)
        );
        localStorage.setItem("configUpdated", true);
      })

      /* ===== USERS ADMIN ===== */
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.allUsers = action.payload;
        localStorage.setItem("gettingUsers", true);
      });
  },
});

/* ================= EXPORTS ================= */

export const {
  logoutUser,
  clearUserError,
} = usersSlice.actions;

export default usersSlice.reducer;
